/**
 * A smooth curve through points, for the exhibit's display transforms and its
 * floor's profiles (M94): Fritsch and Carlson's monotone cubic (1980, *SIAM
 * Journal on Numerical Analysis* 17:238), so it passes through every point,
 * never overshoots between two, and runs one way wherever its points do; the
 * stops of a transform run one way, so it can be turned around. Pure, tested
 * in node (`tests/exhibit.test.ts`).
 */

/** A curve through `xs` (increasing) and `ys`: its value at any x, held at its ends beyond them. */
export function smoothCurve(xs: readonly number[], ys: readonly number[]): (x: number) => number {
  const n = xs.length;
  if (n !== ys.length || n < 2) throw new Error(`a curve needs two points or more, as many x as y (${n}, ${ys.length})`);
  for (let i = 1; i < n; i++) if (!((xs[i] as number) > (xs[i - 1] as number))) throw new Error(`a curve's x must increase: ${xs[i - 1]} then ${xs[i]}`);
  const d = xs.slice(1).map((x, i) => ((ys[i + 1] as number) - (ys[i] as number)) / (x - (xs[i] as number)));
  // Each point's slope: none where the curve turns, else the weighted harmonic mean of the slopes either side.
  const m = ys.map((_, i) => {
    if (i === 0) return d[0] as number;
    if (i === n - 1) return d[n - 2] as number;
    const [d0, d1] = [d[i - 1] as number, d[i] as number];
    if (d0 * d1 <= 0) return 0;
    const [h0, h1] = [(xs[i] as number) - (xs[i - 1] as number), (xs[i + 1] as number) - (xs[i] as number)];
    return (3 * h0 + 3 * h1) / ((2 * h1 + h0) / d0 + (h1 + 2 * h0) / d1);
  });
  return (x: number): number => {
    if (x <= (xs[0] as number)) return ys[0] as number;
    if (x >= (xs[n - 1] as number)) return ys[n - 1] as number;
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if ((xs[mid] as number) <= x) lo = mid;
      else hi = mid;
    }
    const h = (xs[hi] as number) - (xs[lo] as number);
    const s = (x - (xs[lo] as number)) / h;
    const s2 = s * s;
    const s3 = s2 * s;
    return (2 * s3 - 3 * s2 + 1) * (ys[lo] as number) + (s3 - 2 * s2 + s) * h * (m[lo] as number)
      + (-2 * s3 + 3 * s2) * (ys[hi] as number) + (s3 - s2) * h * (m[hi] as number);
  };
}

/** Where an increasing `f` reaches `y` between `lo` and `hi`, by halving (60 halvings: to the last bit of a double over any span the game uses). */
export function solveIncreasing(f: (x: number) => number, y: number, lo: number, hi: number): number {
  let [a, b] = [lo, hi];
  if (y <= f(a)) return a;
  if (y >= f(b)) return b;
  for (let i = 0; i < 60; i++) {
    const mid = (a + b) / 2;
    if (f(mid) < y) a = mid;
    else b = mid;
  }
  return (a + b) / 2;
}

/** A Hermite step from 0 at `a` to 1 at `b`. */
export function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}
